import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePaymentMode } from '../contexts/PaymentModeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Car, Shield, Calendar, Users, Star, MapPin, 
  ChevronRight, CheckCircle2, ArrowRight, Menu, X,
  Bike, Ship, Anchor, Truck, Zap, Coffee, Lock, ShieldCheck, BadgeCheck, ChevronDown
} from 'lucide-react';
import { getDashboardRoute } from '../lib/navigation';
import HireHubLogo, { HireHubIcon } from '../components/HireHubLogo';
import HostEarningsCalculator from '../components/HostEarningsCalculator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Social platform icon SVGs (inline to avoid external dependency)
const SocialIcons = {
  facebook: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
  ),
  tiktok: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
  ),
  twitter: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
  ),
  youtube: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
  ),
};

const LandingPage = () => {
  const { user, logout, isPlatformOwner, isSuperAdmin, isAdmin, isHost } = useAuth();
  const { isHostDirect } = usePaymentMode();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [socialLinks, setSocialLinks] = useState({});
  const [categoryCounts, setCategoryCounts] = useState({});
  const [openFaq, setOpenFaq] = useState(null);

  // Fetch public social links
  useEffect(() => {
    fetch(`${API_URL}/api/public/social-links`)
      .then(r => r.ok ? r.json() : { links: {} })
      .then(d => setSocialLinks(d.links || {}))
      .catch(() => {});
    // Fetch category counts
    fetch(`${API_URL}/api/vehicles/count-by-category`)
      .then(r => r.ok ? r.json() : {})
      .then(d => setCategoryCounts(d))
      .catch(() => {});
  }, []);

  // Role-based dashboard navigation using centralized utility
  const handleGetStarted = () => {
    if (user) {
      const route = getDashboardRoute(user, { isPlatformOwner, isSuperAdmin, isAdmin, isHost });
      navigate(route);
    } else {
      navigate('/register');
    }
  };

  const handleSignOut = () => {
    logout();
    setMobileMenuOpen(false);
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/">
            <HireHubLogo size="sm" />
          </Link>
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            <Link to="/vehicles" className="text-slate-600 hover:text-[#004B8D] transition-colors text-sm lg:text-base">
              Browse Vehicles
            </Link>
            <Link to="/owners/insurance" className="text-slate-600 hover:text-[#004B8D] transition-colors text-sm lg:text-base">
              Insurance Info
            </Link>
          </nav>

          {/* Desktop Buttons */}
          <div className="hidden md:flex items-center gap-2 sm:gap-3">
            {user ? (
              <>
                <Button 
                  onClick={handleGetStarted}
                  className="bg-[#004B8D] hover:bg-[#003865] text-white rounded-xl px-4 sm:px-6 h-9 sm:h-10 text-sm"
                  data-testid="dashboard-btn"
                >
                  Dashboard
                </Button>
                <Button 
                  onClick={handleSignOut}
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 h-9 sm:h-10 text-sm"
                  data-testid="desktop-signout-btn"
                >
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-slate-600 h-9 sm:h-10 text-sm" data-testid="login-btn">
                    Sign In
                  </Button>
                </Link>
                <Button 
                  onClick={() => navigate('/register')}
                  className="bg-[#004B8D] hover:bg-[#003865] text-white rounded-xl px-4 sm:px-6 h-9 sm:h-10 text-sm"
                  data-testid="register-btn"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button 
            variant="ghost" 
            size="icon"
            className="md:hidden h-9 w-9"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white px-4 py-4 space-y-3">
            {user && (
              <>
                {/* Show dashboard links first when logged in */}
                <div className="pb-3 border-b border-slate-100 space-y-1">
                  <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-2">Your Account</p>
                  <button 
                    onClick={() => { handleGetStarted(); setMobileMenuOpen(false); }}
                    className="block w-full text-left text-[#004B8D] font-medium hover:text-[#003865] py-2"
                    data-testid="mobile-dashboard-link"
                  >
                    Go to Dashboard
                  </button>
                  <Link to="/profile" className="block text-slate-600 hover:text-[#004B8D] py-2" onClick={() => setMobileMenuOpen(false)}>
                    My Profile
                  </Link>
                </div>
              </>
            )}
            <Link to="/vehicles" className="block text-slate-600 hover:text-[#004B8D] py-2" onClick={() => setMobileMenuOpen(false)}>
              Browse Vehicles
            </Link>
            <Link to="/owners/insurance" className="block text-slate-600 hover:text-[#004B8D] py-2" onClick={() => setMobileMenuOpen(false)}>
              Insurance Info
            </Link>
            <div className="pt-3 border-t border-slate-100 flex flex-col gap-2">
              {user ? (
                <>
                  <Button 
                    onClick={() => { handleGetStarted(); setMobileMenuOpen(false); }}
                    className="bg-[#004B8D] hover:bg-[#003865] text-white rounded-xl w-full"
                    data-testid="mobile-dashboard-btn"
                  >
                    Dashboard
                  </Button>
                  <Button 
                    onClick={handleSignOut}
                    variant="outline"
                    className="rounded-xl w-full text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="mobile-signout-btn"
                  >
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full rounded-xl">Sign In</Button>
                  </Link>
                  <Button 
                    onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                    className="bg-[#004B8D] hover:bg-[#003865] text-white rounded-xl w-full"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Hero Section — Full-width image background with overlay */}
      <section className="relative pt-20 sm:pt-24 lg:pt-28 pb-14 sm:pb-18 lg:pb-22 overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_07a85c47-9130-4c3c-b515-c625dde3c6cd/artifacts/fbb25dk3_file_000000000e54720b9662a070271335e8.png"
            alt="Premium vehicle available for hire on HireHub Australia"
            className="w-full h-full object-cover object-center"
          />
          {/* Gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/25 lg:to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[460px] sm:min-h-[480px] lg:min-h-[500px]">
            {/* Text Content — subtle backdrop for depth */}
            <div className="animate-fade-in text-center lg:text-left z-10 py-4 sm:py-6 lg:py-0">
              <div className="rounded-3xl bg-black/10 backdrop-blur-[2px] -mx-3 px-3 sm:-mx-5 sm:px-5 py-4 sm:py-5 lg:bg-transparent lg:backdrop-blur-none lg:mx-0 lg:px-0 lg:py-0">
                <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-5 sm:mb-7 border border-white/20">
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                  Australia's Peer-to-Peer Vehicle Marketplace
                </div>
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white tracking-tight mb-5 sm:mb-7 drop-shadow-lg">
                  Hire vehicles from{' '}
                  <span className="text-[#60B5FF]">real hosts</span> near you
                </h1>
                <p className="text-base sm:text-lg text-white/85 leading-relaxed mb-8 sm:mb-10 max-w-lg mx-auto lg:mx-0">
                  Browse cars, bikes, scooters, and more from trusted locals across Australia.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                  <Button 
                    onClick={handleGetStarted}
                    className="bg-[#004B8D] hover:bg-[#003865] text-white shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 rounded-xl font-medium px-6 sm:px-8 py-5 sm:py-6 h-auto text-base sm:text-lg w-full sm:w-auto"
                    data-testid="get-started-btn"
                  >
                    Start Renting <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                  </Button>
                  <Link to="/vehicles" className="w-full sm:w-auto">
                    <Button 
                      variant="outline" 
                      className="border-white/30 text-white bg-white/10 backdrop-blur-sm hover:bg-white/20 hover:border-white/50 hover:scale-[1.02] active:scale-[0.98] rounded-xl font-medium px-6 sm:px-8 py-5 sm:py-6 h-auto text-base sm:text-lg w-full transition-all duration-200 shadow-md"
                      data-testid="browse-vehicles-btn"
                    >
                      Browse Vehicles
                    </Button>
                  </Link>
                </div>

                {/* Trust Indicators */}
                <div className="flex flex-wrap gap-x-5 gap-y-2.5 mt-6 sm:mt-8 justify-center lg:justify-start" data-testid="hero-trust-indicators">
                  <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-white/80">
                    <BadgeCheck className="w-3.5 h-3.5 text-emerald-400" />
                    Verified drivers
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-white/80">
                    <Lock className="w-3.5 h-3.5 text-sky-400" />
                    {isHostDirect ? 'Direct payment mode' : 'Secure payments'}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-white/80">
                    <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
                    Insurance-backed hires
                  </span>
                </div>

                {/* Category Shortcuts */}
                <div className="flex flex-wrap gap-2 mt-5 sm:mt-7 justify-center lg:justify-start" data-testid="hero-category-shortcuts">
                  {[
                    { slug: 'cars',              label: 'Cars',              icon: Car },
                    { slug: 'motorcycles',       label: 'Motorcycles',       icon: Bike },
                    { slug: 'boats',             label: 'Boats',             icon: Ship },
                    { slug: 'trailers',          label: 'Trailers',          icon: Truck },
                    { slug: 'specialty-assets',  label: 'Specialty Assets',  icon: Coffee },
                    { slug: 'ebikes',            label: 'eBikes',            icon: Zap },
                    { slug: 'electric-scooters', label: 'Electric Scooters', icon: Zap },
                    { slug: 'mobility-scooters', label: 'Mobility Scooters', icon: Zap },
                    { slug: 'jetskis',           label: 'Jet Skis',          icon: Anchor },
                  ].map((cat) => (
                    <Link key={cat.slug} to={`/${cat.slug}`} data-testid={`hero-chip-${cat.slug}`}>
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-xs sm:text-sm font-medium text-white/90 hover:bg-white/25 hover:border-white/40 hover:scale-[1.03] active:scale-[0.97] transition-all duration-150 cursor-pointer">
                        <cat.icon className="w-3.5 h-3.5" />
                        {cat.label}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Floating Listing Card — Desktop: bottom right, anchored */}
            <div className="hidden lg:flex justify-end items-end z-10 translate-y-6">
              <div className="bg-white/15 backdrop-blur-xl border border-white/25 rounded-2xl p-3.5 shadow-lg max-w-[210px]" data-testid="hero-listing-card">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-7 h-7 bg-[#004B8D] rounded-lg flex items-center justify-center">
                    <Car className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div>
                    <p className="text-white text-[13px] font-semibold leading-tight">Ssangyong Musso</p>
                    <p className="text-white/60 text-[11px]">Available now</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-white/15">
                  <span className="text-white font-bold text-base">$90<span className="text-white/60 text-[11px] font-normal">/day</span></span>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-0.5">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                      <span className="text-white/90 text-[11px] font-medium">4.8</span>
                    </div>
                    <div className="flex items-center gap-0.5 text-white/60 text-[11px]">
                      <MapPin className="w-2.5 h-2.5" />
                      Gold Coast
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile floating card — anchored at bottom of hero */}
          <div className="lg:hidden mt-8 flex justify-center z-10 relative">
            <div className="bg-white/15 backdrop-blur-xl border border-white/25 rounded-xl px-3.5 py-2.5 shadow-lg inline-flex items-center gap-3 max-w-[85%]" data-testid="hero-listing-card-mobile">
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 bg-[#004B8D] rounded-md flex items-center justify-center flex-shrink-0">
                  <Car className="w-3 h-3 text-white" />
                </div>
                <span className="text-white text-[13px] font-semibold">$90/day</span>
              </div>
              <div className="flex items-center gap-0.5">
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                <span className="text-white/90 text-[11px] font-medium">4.8</span>
              </div>
              <div className="flex items-center gap-0.5 text-white/60 text-[11px]">
                <MapPin className="w-2.5 h-2.5" />
                Gold Coast
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Browse by Category — Moved above Features for discovery-first UX */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 bg-white border-t border-slate-100" data-testid="browse-by-category">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 mb-3">
              Browse by Category
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-sm sm:text-base">
              Find the perfect ride for every occasion
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {[
              { slug: 'cars',              key: 'car',              label: 'Cars',              icon: Car,    bg: 'bg-blue-50 hover:bg-blue-100',    text: 'text-blue-700' },
              { slug: 'motorcycles',       key: 'motorcycle',       label: 'Motorcycles',       icon: Bike,   bg: 'bg-orange-50 hover:bg-orange-100', text: 'text-orange-700' },
              { slug: 'boats',             key: 'boat',             label: 'Boats',             icon: Ship,   bg: 'bg-cyan-50 hover:bg-cyan-100',     text: 'text-cyan-700' },
              { slug: 'jetskis',           key: 'jetski',           label: 'Jet Skis',          icon: Anchor, bg: 'bg-teal-50 hover:bg-teal-100',     text: 'text-teal-700' },
              { slug: 'trailers',          key: 'trailer',          label: 'Trailers',          icon: Truck,  bg: 'bg-slate-100 hover:bg-slate-200',  text: 'text-slate-700' },
              { slug: 'ebikes',            key: 'ebike',            label: 'eBikes',            icon: Zap,    bg: 'bg-green-50 hover:bg-green-100',   text: 'text-green-700' },
              { slug: 'electric-scooters', key: 'electric_scooter', label: 'Electric Scooters', icon: Zap,    bg: 'bg-violet-50 hover:bg-violet-100', text: 'text-violet-700' },
              { slug: 'mobility-scooters', key: 'mobility_scooter', label: 'Mobility Scooters', icon: Zap,    bg: 'bg-rose-50 hover:bg-rose-100',     text: 'text-rose-700' },
              { slug: 'specialty-assets',  key: 'specialty_asset',  label: 'Specialty Assets',  icon: Coffee, bg: 'bg-amber-50 hover:bg-amber-100',   text: 'text-amber-700' },
            ].map((cat) => {
              const count = categoryCounts[cat.key];
              return (
                <Link key={cat.slug} to={`/${cat.slug}`} data-testid={`category-tile-${cat.slug}`}>
                  <div className={`${cat.bg} rounded-xl p-4 sm:p-5 transition-all duration-200 group cursor-pointer`}>
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg ${cat.bg} flex items-center justify-center mb-3`}>
                      <cat.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${cat.text}`} />
                    </div>
                    <p className={`font-semibold text-sm sm:text-base ${cat.text}`}>{cat.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5 group-hover:text-slate-600" data-testid={`category-count-${cat.slug}`}>
                      {count > 0 ? `${count} listing${count !== 1 ? 's' : ''}` : 'Browse listings'}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 mb-3 sm:mb-4">
              Why Choose HireHub Australia
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-sm sm:text-base">
              We make peer-to-peer vehicle hiring simple, safe, and affordable
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[
              {
                icon: Shield,
                title: 'Verified Users',
                description: 'All renters undergo identity verification before they can book any vehicle.'
              },
              {
                icon: Calendar,
                title: 'Flexible Booking',
                description: 'Book for a day, a week, or longer. Hosts set their own availability and rules.'
              },
              {
                icon: Users,
                title: 'Direct Communication',
                description: 'Connect directly with vehicle hosts to arrange pickup and ask questions.'
              }
            ].map((feature, index) => (
              <Card key={index} className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden group">
                <CardContent className="p-5 sm:p-6 lg:p-8">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#004B8D]/10 rounded-xl flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-[#004B8D] transition-colors">
                    <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#004B8D] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900 mb-2 sm:mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 mb-3 sm:mb-4">
              How It Works
            </h2>
            <p className="text-slate-600 text-sm sm:text-base">Get on the road in three simple steps</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 sm:gap-8">
            {[
              { step: '1', title: 'Search', description: 'Browse vehicles by location, type, and dates' },
              { step: '2', title: 'Book', description: 'Request to book and wait for host approval' },
              { step: '3', title: 'Drive', description: 'Complete handover checklist and hit the road' }
            ].map((item, index) => (
              <div key={index} className="relative text-center sm:text-left">
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 bg-[#004B8D] rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    {item.step}
                  </div>
                  {index < 2 && (
                    <div className="hidden sm:block flex-1 h-0.5 bg-slate-200 relative">
                      <ChevronRight className="w-5 h-5 text-slate-400 absolute -right-1 -top-2" />
                    </div>
                  )}
                </div>
                <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm sm:text-base">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Host Earnings Calculator Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 bg-slate-50" id="host-earnings">
        <div className="max-w-6xl mx-auto">
          <HostEarningsCalculator variant="default" showHeading={true} />
        </div>
      </section>

      {/* List Your Vehicle CTA */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 bg-[#004B8D]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-white text-center lg:text-left order-2 lg:order-1">
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold mb-3 sm:mb-4">
                Ready to start earning?
              </h2>
              <p className="text-white/80 text-sm sm:text-lg mb-4 sm:mb-6">
                List your car and earn money when you're not using it. You set the rules, rates, and availability.
              </p>
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8 text-left max-w-md mx-auto lg:mx-0">
                {[
                  'Set your own daily rates',
                  'Control your availability calendar',
                  'Define your rules and requirements',
                  isHostDirect ? 'Get paid directly by renters' : 'Secure payment processing'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-2 sm:gap-3 text-sm sm:text-base">
                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#00A3BF] flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate('/register')}
                className="bg-white text-[#004B8D] hover:bg-slate-100 rounded-xl font-medium px-6 sm:px-8 py-5 sm:py-6 h-auto text-base sm:text-lg w-full sm:w-auto"
                data-testid="list-vehicle-btn"
              >
                List Your Vehicle
              </Button>
            </div>
            <div className="relative order-1 lg:order-2">
              <img 
                src="https://images.unsplash.com/photo-1618839112829-9dea81b885ad?crop=entropy&cs=srgb&fm=jpg&q=85" 
                alt="Vehicle host"
                className="rounded-2xl shadow-2xl w-full h-[220px] sm:h-[280px] lg:h-[350px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 bg-slate-50 border-t border-slate-100" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 mb-3">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-500 text-sm sm:text-base">
              Everything you need to know before booking on HireHub
            </p>
          </div>
          <div className="space-y-3">
            {[
              {
                q: 'How does booking a vehicle work?',
                a: 'Browse available vehicles, select your dates, and submit a booking request. The host will review and approve your request. Once confirmed, you\'ll receive pickup details and can collect the vehicle at the agreed time and location.'
              },
              {
                q: 'When do I pay for a booking?',
                a: isHostDirect
                  ? 'Payment is arranged directly with the host once they\'ve accepted your booking request (for example via PayID or bank transfer). HireHub does not process or hold funds for these bookings — the host confirms receipt in HireHub and your booking is secured.'
                  : 'Payment is collected securely at the time of booking confirmation. Your card is charged only after the host accepts your request. All transactions are processed through our trusted payment partner.'
              },
              {
                q: 'Is my payment secure?',
                a: isHostDirect
                  ? 'In direct payment mode, payments are arranged directly between the host and renter (for example via PayID or bank transfer). HireHub does not process or store payment details for these bookings. Both sides can use HireHub to record payment confirmation, bond collection, pre/post-hire evidence, and dispute history.'
                  : 'Yes. HireHub uses industry-leading payment processing for all transactions. Your card details are never stored on our servers. Our payment partner is PCI-DSS Level 1 certified — the highest level of security in the payments industry.'
              },
              {
                q: 'Why do I need to upload ID?',
                a: 'Identity verification protects both renters and hosts. We verify your identity to prevent fraud, ensure accountability, and build trust within the community. Your documents are stored securely and never shared with other users.'
              },
              {
                q: 'What happens if there is damage?',
                a: 'Vehicle hosts are responsible for maintaining their own insurance coverage. If damage occurs during a hire, the renter and host should document the issue and contact their respective insurers. HireHub is a marketplace and does not provide insurance — we facilitate the connection between parties.'
              },
              {
                q: 'Can I cancel a booking?',
                a: isHostDirect
                  ? 'Yes. Cancellation terms depend on the host\'s chosen cancellation policy (flexible, moderate, or strict) — viewable on each listing before booking. Because HireHub does not process payments in direct-payment mode, any refunds are arranged directly between the host and renter using the policy as a reference. HireHub keeps the booking record and evidence to support either side if a dispute arises.'
                  : 'Yes. Cancellation terms depend on the host\'s chosen cancellation policy (flexible, moderate, or strict). You can view the applicable policy on each vehicle listing before booking. Refund amounts vary depending on how far in advance you cancel.'
              },
              {
                q: 'What if something goes wrong during my hire?',
                a: 'Contact the host directly through HireHub\'s messaging system. If you\'re unable to resolve the issue, reach out to our support team via the Contact Us page. We\'re here to help mediate and ensure a fair outcome for both parties.'
              }
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden transition-shadow duration-200 hover:shadow-sm"
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                  data-testid={`faq-toggle-${i}`}
                  aria-expanded={openFaq === i}
                >
                  <span className="font-medium text-sm sm:text-base text-slate-900">{item.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <div
                  className={`grid transition-all duration-200 ease-in-out ${openFaq === i ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                >
                  <div className="overflow-hidden">
                    <p className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{item.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-12 px-4 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div className="col-span-2 sm:col-span-1">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <HireHubIcon size={40} className="rounded-lg" />
                <span className="font-heading font-bold text-lg sm:text-xl">HireHub</span>
              </div>
              <p className="text-slate-400 text-xs sm:text-sm">
                Australia's trusted peer-to-peer vehicle hire marketplace.
              </p>
              {/* Dynamic Follow Us */}
              {Object.keys(socialLinks).filter(k => socialLinks[k]).length > 0 && (
                <div className="mt-4" data-testid="footer-social-links">
                  <p className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2.5">Follow us</p>
                  <div className="flex items-center gap-3">
                    {Object.entries(socialLinks).map(([platform, url]) => {
                      if (!url || !SocialIcons[platform]) return null;
                      return (
                        <a
                          key={platform}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-400 hover:text-white transition-colors"
                          data-testid={`footer-social-${platform}`}
                          aria-label={platform}
                        >
                          {SocialIcons[platform]}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            
            <div>
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">For Renters</h4>
              <ul className="space-y-2 text-slate-400 text-xs sm:text-sm">
                <li><Link to="/vehicles" className="hover:text-white transition-colors">Browse Vehicles</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Sign Up</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">For Hosts</h4>
              <ul className="space-y-2 text-slate-400 text-xs sm:text-sm">
                <li><Link to="/register" className="hover:text-white transition-colors">List Your Vehicle</Link></li>
                <li><Link to="/owners/insurance" className="hover:text-white transition-colors" data-testid="footer-host-insurance">Insurance Info</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Legal</h4>
              <ul className="space-y-2 text-slate-400 text-xs sm:text-sm">
                <li><Link to="/legal/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/legal/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/legal/insurance" className="hover:text-white transition-colors" data-testid="footer-legal-insurance">Insurance Information</Link></li>
                <li><Link to="/legal/community-guidelines" className="hover:text-white transition-colors" data-testid="footer-community-guidelines">Community Guidelines</Link></li>
                <li><Link to="/legal/refund-policy" className="hover:text-white transition-colors" data-testid="footer-refund-policy">Refund Policy</Link></li>
                <li><Link to="/trust-safety" className="hover:text-white transition-colors" data-testid="footer-trust-safety">Trust & Safety</Link></li>
                <li><Link to="/renter-agreement" className="hover:text-white transition-colors" data-testid="footer-renter-agreement">Renter Agreement</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors" data-testid="footer-contact">Contact Us</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-6 sm:pt-8 text-center text-slate-500 text-xs sm:text-sm">
            <p>© {new Date().getFullYear()} HireHub Australia. All rights reserved.</p>
            <p className="mt-2">HireHub Australia is a marketplace platform and is not an insurer.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
